import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getStoredUser } from '@/lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INBOUND_REASONS = [
  { value: 'purchase', label: 'Purchase / Restock' },
  { value: 'return_from_customer', label: 'Customer Return' },
  { value: 'transfer_in', label: 'Transfer In' },
  { value: 'count_correction', label: 'Count Correction' },
  { value: 'other', label: 'Other' },
];

const OUTBOUND_REASONS_INTERNAL = [
  { value: 'usage', label: 'Internal Usage' },
  { value: 'damaged', label: 'Damaged / Disposed' },
  { value: 'lost', label: 'Lost / Missing' },
  { value: 'return_to_supplier', label: 'Return to Supplier' },
  { value: 'transfer_out', label: 'Transfer Out' },
  { value: 'other', label: 'Other' },
];

const OUTBOUND_REASONS_CUSTOMER = [
  { value: 'damaged', label: 'Damaged / Disposed' },
  { value: 'lost', label: 'Lost / Missing' },
  { value: 'return_to_supplier', label: 'Return to Supplier' },
  { value: 'transfer_out', label: 'Transfer Out' },
  { value: 'other', label: 'Other' },
];

/**
 * StockMovementDialog - reusable dialog for inbound / outbound movements.
 *
 * Props:
 *  - open, onOpenChange
 *  - product: the product being adjusted
 *  - movementType: 'in' or 'out'
 *  - onSuccess: callback after successful save
 */
export default function StockMovementDialog({
  open,
  onOpenChange,
  product,
  movementType,
  onSuccess,
  currentUser,
}) {
  const effectiveUser = useMemo(() => currentUser || getStoredUser(), [currentUser]);
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState([]);
  const [requesters, setRequesters] = useState([]);
  const [formData, setFormData] = useState({
    quantity: 1,
    unit_price: '',
    reason: '',
    notes: '',
    performed_by: '',
    supplier: '',
    requestor: '',
  });

  // Load users (operators) and requesters when the dialog opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [uRes, rRes] = await Promise.all([
          axios.get(`${API}/users/roster`),
          axios.get(`${API}/requesters?active_only=true`),
        ]);
        const activeUsers = (uRes.data || []);
        setOperators(activeUsers);
        setRequesters(rRes.data || []);
      } catch (err) {
        // Users endpoint requires users.manage — fall back to just the current user
        // for operators, so operators can still submit movements.
        if (effectiveUser) setOperators([effectiveUser]);
        try {
          const rRes = await axios.get(`${API}/requesters?active_only=true`);
          setRequesters(rRes.data || []);
        } catch {
          setRequesters([]);
        }
      }
    })();
  }, [open, effectiveUser]);

  useEffect(() => {
    if (open) {
      setFormData({
        quantity: 1,
        unit_price: '',
        reason: '',
        notes: '',
        performed_by: effectiveUser?.name || effectiveUser?.email || '',
        supplier: product?.supplier || '',
        requestor: '',
      });
    }
  }, [open, product, effectiveUser]);

  if (!product) return null;

  const isInbound = movementType === 'in';
  const isInternal = product.inventory_type === 'internal';

  const reasonOptions = isInbound
    ? INBOUND_REASONS
    : isInternal
    ? OUTBOUND_REASONS_INTERNAL
    : OUTBOUND_REASONS_CUSTOMER;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.quantity || parseInt(formData.quantity) <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    if (isInbound && (!formData.unit_price || parseFloat(formData.unit_price) < 0)) {
      toast.error('Unit price is required for inbound (used to compute weighted average cost)');
      return;
    }
    if (!isInbound && isInternal && !formData.requestor.trim()) {
      toast.error('Requestor is required for internal outbound entries');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        product_id: product.id,
        adjustment_type: movementType,
        quantity: parseInt(formData.quantity),
        reason: formData.reason || null,
        notes: formData.notes || null,
        performed_by: formData.performed_by || null,
      };
      if (isInbound) {
        payload.unit_price = parseFloat(formData.unit_price);
        if (formData.supplier) payload.supplier = formData.supplier;
      }
      if (!isInbound && isInternal) payload.requestor = formData.requestor;

      await axios.post(`${API}/stock-adjustments`, payload);
      toast.success(
        isInbound ? 'Inbound recorded successfully' : 'Outbound recorded successfully'
      );
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to record movement:', error);
      toast.error(error.response?.data?.detail || 'Failed to record movement');
    } finally {
      setLoading(false);
    }
  };

  const accentColor = isInbound ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700';
  const testIdSuffix = isInbound ? 'inbound' : 'outbound';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isInbound ? (
              <ArrowUp className="h-5 w-5 text-green-600" />
            ) : (
              <ArrowDown className="h-5 w-5 text-red-600" />
            )}
            {isInbound ? 'Record Inbound Stock' : 'Record Outbound Stock'}
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium">{product.name}</span> ({product.sku}) — Current: <strong>{product.quantity}</strong> {product.unit_of_measure || 'unit'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Quantity *</Label>
            <Input
              type="number"
              min="1"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              required
              data-testid={`${testIdSuffix}-quantity-input`}
            />
          </div>

          {isInbound && (
            <div className="space-y-2">
              <Label>Unit Price (GHS) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Cost per unit"
                value={formData.unit_price}
                onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                required
                data-testid={`${testIdSuffix}-unit-price-input`}
              />
              {formData.unit_price && formData.quantity && (
                <p className="text-xs text-slate-600">
                  Total cost: <strong>GHS {(parseFloat(formData.unit_price || 0) * parseInt(formData.quantity || 0)).toFixed(2)}</strong>
                  {' · '}Weighted avg will be recomputed automatically
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Reason</Label>
            <Select
              value={formData.reason}
              onValueChange={(v) => setFormData({ ...formData, reason: v })}
            >
              <SelectTrigger data-testid={`${testIdSuffix}-reason-select`}>
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {reasonOptions.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isInbound && (
            <div className="space-y-2">
              <Label>Supplier</Label>
              <Input
                placeholder="Supplier name"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                data-testid={`${testIdSuffix}-supplier-input`}
              />
            </div>
          )}

          {!isInbound && isInternal && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Requestor *</Label>
                <Link
                  to="/requesters"
                  className="text-xs text-[#1680e5] hover:underline flex items-center gap-1"
                  data-testid="manage-requesters-link"
                >
                  <UserPlus className="h-3 w-3" /> Manage requesters
                </Link>
              </div>
              {requesters.length === 0 ? (
                <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded p-3">
                  No requesters yet. <Link to="/requesters" className="text-[#1680e5] hover:underline">Add one</Link> so you can log this outbound.
                </div>
              ) : (
                <Select
                  value={formData.requestor}
                  onValueChange={(v) => setFormData({ ...formData, requestor: v })}
                >
                  <SelectTrigger data-testid={`${testIdSuffix}-requestor-input`}>
                    <SelectValue placeholder="Select requestor" />
                  </SelectTrigger>
                  <SelectContent>
                    {requesters.map((r) => (
                      <SelectItem key={r.id} value={r.name}>
                        {r.name}{r.department ? ` — ${r.department}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-slate-500">
                The person or team receiving this item
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Performed By</Label>
            {operators.length === 0 ? (
              <Input
                placeholder="Your name"
                value={formData.performed_by}
                onChange={(e) => setFormData({ ...formData, performed_by: e.target.value })}
                data-testid={`${testIdSuffix}-performed-by-input`}
              />
            ) : (
              <Select
                value={formData.performed_by}
                onValueChange={(v) => setFormData({ ...formData, performed_by: v })}
              >
                <SelectTrigger data-testid={`${testIdSuffix}-performed-by-input`}>
                  <SelectValue placeholder="Select operator" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((op) => (
                    <SelectItem key={op.id || op.email} value={op.name || op.email}>
                      {op.name || op.email}
                      {op.role ? <span className="text-xs text-slate-500 ml-2">({op.role.replace('_', ' ')})</span> : null}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              placeholder="Optional notes (e.g., PO number, delivery reference)"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              data-testid={`${testIdSuffix}-notes-input`}
            />
          </div>

          <Button
            type="submit"
            className={`w-full ${accentColor}`}
            disabled={loading}
            data-testid={`submit-${testIdSuffix}-button`}
          >
            {loading ? 'Recording...' : isInbound ? 'Record Inbound' : 'Record Outbound'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
